/**
 * LinkedInPublishButton — publier une Breaking News validée sur son profil
 * LinkedIn personnel, et pour l'auteur, ouvrir le relais aux autres membres.
 *
 * RÈGLE MÉTIER
 *   - Le bouton n'existe que si le post est `published`, donc validé par la
 *     hiérarchie. Avant validation, rien.
 *   - L'AUTEUR peut publier son post quand il veut.
 *   - Les AUTRES membres ne peuvent le relayer que si l'auteur l'a autorisé.
 *     C'est un opt-in de l'auteur, jamais implicite.
 *   - Chacun ne publie qu'une fois, mais la publication de l'un n'empêche
 *     pas celle des autres : l'idempotence porte sur (post, membre).
 *
 * POURQUOI LE RELAIS : un membre qui cherche une mise en relation précise a
 * intérêt à ce que son post soit repris par plusieurs profils. Le compteur
 * de relais lui montre la diffusion réelle.
 *
 * Le backend revérifie toutes ces règles.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ExternalLink, Linkedin, Loader2, Share2, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { stripMarkdown } from "../lib/markdown";

const MAX = 3000;

export default function LinkedInPublishButton({ post, status, onPublished }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState(null);
  const [savingRelay, setSavingRelay] = useState(false);

  const postId = post?.post_id;
  const publishable = post?.status === "published";

  // Le texte est reconstitué à l'identique de ce que compose le backend
  // (titre + contenu sans markdown), pour que le compteur dise vrai.
  const text = useMemo(() => {
    if (!post) return "";
    return [post.title, stripMarkdown(post.content, true)]
      .filter(Boolean)
      .join("\n\n");
  }, [post]);

  const load = useCallback(async () => {
    if (!postId || !publishable) return;
    try {
      const { data } = await api.get(`/posts/${postId}/linkedin-publication`);
      setState(data);
    } catch {
      setState(null);
    }
  }, [postId, publishable]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Conditions d'affichage ------------------------------------------------
  if (!post || !publishable) return null;          // pas validé → rien
  if (!status || status.visible === false) return null;
  if (status.admin_only) return null;              // prod non configurée
  if (!state) return null;

  const { published, post_url, is_author, relay_allowed, relay_count } = state;
  const tooLong = text.length > MAX;
  const mockMode = !status.configured;
  const needsConnect = status.configured && !status.connected;
  const needsReauth = status.reauth_required;
  const canPublish = is_author || relay_allowed;

  const doPublish = async () => {
    setBusy(true);
    setConfirming(false);
    try {
      const { data } = await api.post(`/posts/${postId}/publish-linkedin`, {
        include_image: true,
      });
      toast.success(
        data.mock ? "Publication simulée (LinkedIn non configuré)." : "Publié sur LinkedIn."
      );
      onPublished?.(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la publication sur LinkedIn.");
    } finally {
      setBusy(false);
    }
  };

  const toggleRelay = async () => {
    setSavingRelay(true);
    try {
      const { data } = await api.post(`/posts/${postId}/linkedin-relay`, {
        allowed: !relay_allowed,
      });
      setState((s) => ({ ...s, relay_allowed: data.relay_allowed }));
      toast.success(
        data.relay_allowed
          ? "Les membres peuvent maintenant relayer ce post sur LinkedIn."
          : "Relais désactivé."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally {
      setSavingRelay(false);
    }
  };

  const relayLine = relay_count > 0 && (
    <span
      className="inline-flex items-center gap-1.5 text-xs"
      style={{ color: "#666" }}
      data-testid="linkedin-relay-count"
    >
      <Users size={12} />
      {relay_count} membre{relay_count > 1 ? "s" : ""} {relay_count > 1 ? "ont" : "a"}{" "}
      relayé ce post sur LinkedIn
    </span>
  );

  // --- Interrupteur d'autorisation, réservé à l'auteur -----------------------
  const relayToggle = is_author && (
    <div
      className="mt-3 p-3 rounded-lg flex items-start gap-3 flex-wrap"
      style={{ background: "#FAFAFA", border: "1px solid #E5E5E5" }}
      data-testid="linkedin-relay-toggle"
    >
      <Share2 size={16} color="#F77F00" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
          Autoriser les membres à relayer ce post
        </div>
        <p className="text-xs mt-0.5" style={{ color: "#666" }}>
          Chaque membre pourra le publier sur son propre profil LinkedIn. Plus
          un post est relayé, plus il porte loin — utile pour une demande de
          mise en relation.
        </p>
      </div>
      <button
        type="button"
        onClick={toggleRelay}
        disabled={savingRelay}
        aria-pressed={relay_allowed}
        data-testid="linkedin-relay-switch"
        className="px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors disabled:opacity-60"
        style={{
          background: relay_allowed ? "#F77F00" : "#FFF",
          color: relay_allowed ? "#FFF" : "#1A1A1A",
          borderColor: relay_allowed ? "#F77F00" : "#E5E5E5",
        }}
      >
        {savingRelay ? "…" : relay_allowed ? "Autorisé" : "Autoriser"}
      </button>
    </div>
  );

  // --- Ce membre a déjà publié ------------------------------------------------
  if (published) {
    return (
      <div className="mt-4" data-testid="linkedin-published">
        <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color: "#666" }}>
          <Linkedin size={14} color="#0A66C2" />
          <span>Vous avez publié ce post sur LinkedIn.</span>
          {post_url && (
            <a
              href={post_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold underline underline-offset-2"
              style={{ color: "#0A66C2" }}
              data-testid="linkedin-view-post"
            >
              Voir sur LinkedIn <ExternalLink size={12} />
            </a>
          )}
        </div>
        {relayLine && <div className="mt-1.5">{relayLine}</div>}
        {relayToggle}
      </div>
    );
  }

  // --- Pas encore publié par ce membre ---------------------------------------
  return (
    <div className="mt-4" data-testid="linkedin-publish-block">
      {!canPublish ? (
        <p className="text-xs" style={{ color: "#999" }} data-testid="linkedin-relay-closed">
          L'auteur n'a pas ouvert le relais de ce post sur LinkedIn.
        </p>
      ) : (
        <>
          {needsReauth && (
            <p className="text-xs mb-2" style={{ color: "#991B1B" }}>
              Reconnexion LinkedIn nécessaire (voir votre profil).
            </p>
          )}
          {needsConnect && (
            <p className="text-xs mb-2" style={{ color: "#666" }}>
              Connectez votre compte LinkedIn depuis votre profil pour publier.
            </p>
          )}

          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={busy || tooLong || needsConnect || needsReauth}
              data-testid="linkedin-publish"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors hover:bg-[#F0F6FC] disabled:opacity-50"
              style={{ borderColor: "#E5E5E5", color: "#1A1A1A" }}
            >
              <Linkedin size={14} color="#0A66C2" />
              {mockMode
                ? "Publier sur LinkedIn (simulation)"
                : is_author
                ? "Publier sur LinkedIn"
                : "Relayer sur mon LinkedIn"}
            </button>
          ) : (
            <div
              className="p-4 rounded-lg"
              style={{ background: "#F0F6FC", border: "1px solid #CFE2F5" }}
              data-testid="linkedin-confirm"
            >
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={16} color="#0A66C2" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm" style={{ color: "#1A1A1A" }}>
                  {mockMode
                    ? "Mode simulation : rien ne partira réellement sur LinkedIn."
                    : "Ce post va être publié sur votre profil LinkedIn personnel, visible publiquement. C'est irréversible depuis Break News."}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="px-4 py-2 rounded-full text-sm font-semibold border bg-white"
                  style={{ borderColor: "#E5E5E5" }}
                  data-testid="linkedin-cancel"
                >
                  Annuler
                </button>
                <button
                  onClick={doPublish}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#0A66C2" }}
                  data-testid="linkedin-confirm-publish"
                >
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {busy ? "Publication…" : "Oui, publier"}
                </button>
              </div>
            </div>
          )}

          <div
            className="text-xs mt-1.5"
            style={{ color: tooLong ? "#EF4444" : "#999" }}
            data-testid="linkedin-char-counter"
          >
            {text.length} / {MAX} caractères
            {tooLong && ` — raccourcissez de ${text.length - MAX}`}
          </div>
        </>
      )}

      {relayLine && <div className="mt-2">{relayLine}</div>}
      {relayToggle}
    </div>
  );
}
